import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, AtSign } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const EXEMPT_PREFIXES = ["/auth", "/reset-password", "/u/"];

const normalize = (v: string) => v.trim().toLowerCase().replace(/[^a-z0-9_]/g, "");

export const UsernameGate = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [needsUsername, setNeedsUsername] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const exempt = EXEMPT_PREFIXES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    let cancelled = false;
    if (loading || !user || exempt) {
      setNeedsUsername(false);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("username, full_name")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      if (data && !data.username?.trim()) {
        const seed = normalize(
          (data.full_name || user.email?.split("@")[0] || "").slice(0, 20),
        );
        setValue(seed);
        setNeedsUsername(true);
      } else {
        setNeedsUsername(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, loading, exempt, location.pathname]);

  const handleSave = async () => {
    if (!user) return;
    const clean = normalize(value);
    if (clean.length < 3) {
      toast.error("Username must be at least 3 characters (letters, numbers, _)");
      return;
    }
    if (clean.length > 20) {
      toast.error("Username must be 20 characters or fewer");
      return;
    }

    setSaving(true);
    try {
      const { data: taken } = await supabase.rpc("get_public_profile_by_username", {
        _username: clean,
      });
      if (Array.isArray(taken) && taken.length > 0) {
        toast.error("That username is already taken");
        return;
      }

      const { error } = await supabase
        .from("profiles")
        .update({ username: clean })
        .eq("user_id", user.id);
      if (error) throw error;

      toast.success(`You're @${clean}`);
      setNeedsUsername(false);
    } catch {
      toast.error("Could not save username. Try another one.");
    } finally {
      setSaving(false);
    }
  };

  if (!needsUsername) return null;

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        className="w-[calc(100vw-2rem)] max-w-sm rounded-2xl [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Pick your username</DialogTitle>
          <DialogDescription>
            This is how friends find you and how you show up across SpotrAI.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="relative">
            <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              value={value}
              onChange={(e) => setValue(normalize(e.target.value))}
              placeholder="username"
              maxLength={20}
              className="pl-9"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
              }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            3–20 characters. Letters, numbers and underscores only.
          </p>
          <Button className="w-full" onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
